import { useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';

const Hero = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hero, setHero] = useState<{
    title: string;
    subtitle: string;
    services_button_label: string;
    contact_button_label: string;
    scroll_text: string;
    image_url: string;
  } | null>(null);

  useEffect(() => {
    // Smooth scroll for anchor links
    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target.hash && target.hash !== '#') {
        e.preventDefault();
        const element = document.querySelector(target.hash);
        if (element) {
          window.scrollTo({
            top: element.getBoundingClientRect().top + window.scrollY - 80,
            behavior: 'smooth'
          });
        }
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', handleAnchorClick);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', handleAnchorClick);
      });
    };
  }, []);

  useEffect(() => {
    const API_BASE = (import.meta as any)?.env?.VITE_API_URL || (typeof window !== 'undefined' && window.location && window.location.port === '5173' ? 'http://localhost:3002' : '');
    const loadHero = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/hero`, { credentials: 'include' });
        const json = await res.json();
        if (json?.ok && json?.data) {
          setHero(json.data);
        }
      } catch (e: any) {
        console.error('Failed to load hero content', e?.message || e);
        setError('Peadsisule ei pääse ligi');
      } finally {
        setLoading(false);
      }
    };
    loadHero();
  }, []);

  return (
    <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        {loading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-gray-500">Loading hero image...</div>
          </div>
        ) : (
          <>
            <img 
              src={hero?.image_url || ''}
              alt="Metsamaastik" 
              className="w-full h-full object-cover"
              fetchPriority="high"
              onLoad={() => console.log('Hero image loaded successfully:', hero?.image_url)}
              onError={(_) => {
                console.error('Hero image failed to load:', hero?.image_url);
                // In DB-only mode we simply show nothing if it fails
                setError('Pildi laadimine ebaõnnestus');
              }}
            />
            {error && (
              <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded text-sm">
                {error}
              </div>
            )}
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 to-primary-900/50"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="animate-float">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 drop-shadow-lg">
            {hero?.title || t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 drop-shadow">
            {hero?.subtitle || t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/teenused" className="px-8 py-3 bg-primary-700 hover:bg-primary-800 text-white rounded-lg shadow-lg transition-all hover:scale-105 font-medium">
              {hero?.services_button_label || t('hero.servicesButton')}
            </a>
            <a href="/kontakt" className="px-8 py-3 bg-white/90 hover:bg-white text-primary-800 rounded-lg shadow-lg transition-all hover:scale-105 font-medium">
              {hero?.contact_button_label || t('hero.contactButton')}
            </a>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center">
        <span className="text-white/80 text-sm mb-2">{hero?.scroll_text || t('hero.scrollText')}</span>
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1">
          <div className="w-1.5 h-3 bg-white/80 rounded-full animate-bounce"></div>
        </div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute -bottom-2 left-0 right-0 z-10">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
          <path fill="#f9fafb" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,90.7C640,96,800,96,960,85.3C1120,75,1280,53,1360,42.7L1440,32L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;